import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthContext';
import { config } from '../lib/config';
import { CLIENT_TYPES, type SignupClientType } from '../lib/constants';
import { ApiError, confirmSignup, resendSignupCode, signup } from '../lib/identityAuth';
import { PasswordField } from '../components/PasswordField';
import { BrandLogo } from '../components/BrandLogo';
import {
  loadRedirectContext,
  navigatePostLogin,
  resolvePostLoginDestination,
} from '../lib/redirect';

type Step = 'details' | 'verify';

const RESEND_SECONDS = config.resendCooldownSeconds;

function messageForError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'EMAIL_ALREADY_REGISTERED':
        return 'This email is already registered. Sign in instead.';
      case 'VALIDATION_ERROR':
        return fallback;
      case 'RESEND_THROTTLED':
        return 'Please wait before requesting another code.';
      case 'PROMOTE_RETRY_FAILED':
        return 'Account setup is finishing. Wait a moment and try again.';
      default:
        return err.message || fallback;
    }
  }
  return err instanceof Error ? err.message : fallback;
}

export function SignUpPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [step, setStep] = useState<Step>('details');
  const [displayName, setDisplayName] = useState('');
  const [clientType, setClientType] = useState<SignupClientType>(CLIENT_TYPES.INDIVIDUAL);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [address, setAddress] = useState('');
  const [nit, setNit] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    loadRedirectContext();
  }, []);

  const startCooldown = useCallback((seconds: number) => {
    setCooldown(seconds);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown((current) => {
        if (current <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return current - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  async function handleDetails(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await signup({
        email,
        password,
        displayName,
        clientType,
        address,
        nit,
        city,
        country,
      });
      setStep('verify');
      setNotice(`We sent a verification code to ${email}.`);
      startCooldown(RESEND_SECONDS);
    } catch (err) {
      setError(messageForError(err, 'Check your details. Password must meet the security policy.'));
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      await confirmSignup(email, code.trim());
      await login(email, password);
      const destination = await resolvePostLoginDestination();
      navigatePostLogin(destination, navigate);
    } catch (err) {
      setError(messageForError(err, 'Invalid or expired code.'));
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    if (cooldown > 0) return;
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      await resendSignupCode(email);
      setNotice('A new code is on its way.');
      startCooldown(RESEND_SECONDS);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'RESEND_THROTTLED') {
        const retry = Number(err.details?.retry_after_seconds) || RESEND_SECONDS;
        startCooldown(retry);
        setError(`Please wait ${retry}s before requesting another code.`);
      } else {
        setError(messageForError(err, 'Could not resend the code.'));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-screen">
      <div className="login-card">
        <BrandLogo />
        <h1>{step === 'details' ? 'Create your account' : 'Verify your email'}</h1>
        <p className="login-card__copy">
          {step === 'details'
            ? 'Register a Sense AI client account. You will become the account owner (master user).'
            : `Enter the verification code we emailed to ${email} to activate your account.`}
        </p>

        {step === 'details' ? (
          <form className="login-form" onSubmit={handleDetails}>
            <label>
              <span>Account name</span>
              <input
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                type="text"
                required
                autoComplete="organization"
              />
            </label>

            <label>
              <span>Account type</span>
              <select
                value={clientType}
                onChange={(event) => setClientType(event.target.value as SignupClientType)}
              >
                <option value={CLIENT_TYPES.INDIVIDUAL}>Individual</option>
                <option value={CLIENT_TYPES.ORGANIZATION}>Organization</option>
              </select>
            </label>

            <label>
              <span>Address</span>
              <input
                value={address}
                onChange={(event) => setAddress(event.target.value)}
                type="text"
                required
                autoComplete="street-address"
              />
            </label>

            <label>
              <span>NIT</span>
              <input
                value={nit}
                onChange={(event) => setNit(event.target.value)}
                type="text"
                required
              />
            </label>

            <label>
              <span>City</span>
              <input
                value={city}
                onChange={(event) => setCity(event.target.value)}
                type="text"
                required
                autoComplete="address-level2"
              />
            </label>

            <label>
              <span>Country</span>
              <input
                value={country}
                onChange={(event) => setCountry(event.target.value)}
                type="text"
                required
                autoComplete="country-name"
              />
            </label>

            <label>
              <span>Email</span>
              <input
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                type="email"
                required
                autoComplete="username"
              />
            </label>

            <PasswordField
              label="Password"
              value={password}
              onChange={setPassword}
              minLength={config.passwordMinLength}
              autoComplete="new-password"
            />

            <PasswordField
              label="Confirm password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              minLength={config.passwordMinLength}
              autoComplete="new-password"
            />

            {error ? <div className="form-error">{error}</div> : null}
            {notice ? <p className="hint-text">{notice}</p> : null}

            <button className="primary-button" disabled={loading} type="submit">
              {loading ? 'Creating account...' : 'Create account'}
            </button>
          </form>
        ) : (
          <form className="login-form" onSubmit={handleVerify}>
            <label>
              <span>Verification code</span>
              <input
                value={code}
                onChange={(event) => setCode(event.target.value)}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
              />
            </label>

            {error ? <div className="form-error">{error}</div> : null}
            {notice ? <p className="hint-text">{notice}</p> : null}

            <button className="primary-button" disabled={loading} type="submit">
              {loading ? 'Verifying...' : 'Verify and continue'}
            </button>

            <button
              className="secondary-button"
              type="button"
              disabled={loading || cooldown > 0}
              onClick={handleResend}
            >
              {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
            </button>
          </form>
        )}

        <p className="hint-text">
          Already have an account?{' '}
          <Link className="text-link" to={`/login${location.search}`}>
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
